import { DatePicker, Input } from "antd";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { useQueryParam, NumberParam } from "use-query-params";
import moment from "moment";

import { ModifiedAntTable } from ".";
import { useFilterDateDropdown, useListTitle } from "../hooks";
import { getIdFieldAndId } from "../utils";
import { getCollectionsByDateAction } from "../store/loans";

const PAGE_SIZE = 50;

const CollectionsByDateList = () => {
  const [dateRange, setDateRange] = useState([moment(), moment()]);
  const [filterName, setFilterName] = useState("");
  const [page, setPage] = useQueryParam("page", NumberParam);

  const dispatch = useDispatch();
  const { orgType, orgId } = useParams();
  const { org_id_field, org_id } = getIdFieldAndId(orgType, orgId);
  const { fullListName } = useListTitle("collections");

  const {
    loans: { loadingCollections, collections, collectionsTotals },
  } = useSelector((state) => ({ loans: state.loans }));

  const { renderFilter } = useFilterDateDropdown({ setFilterDate: setDateRange });

  const currentPage = Number(page) || 1;
  const totals = collectionsTotals?.[org_id_field]?.[org_id];
  const records = collections?.[org_id_field]?.[org_id] ?? [];

  const getData = (pageNum = 1) => {
    const queryString = [
      `${org_id_field}=${org_id}`,
      `start_date=${dateRange?.[0]?.format("YYYY/MM/DD")}`,
      `end_date=${dateRange?.[1]?.format("YYYY/MM/DD")}`,
      `page=${pageNum}`,
      `page_size=${PAGE_SIZE}`,
      filterName ? `name=${filterName}` : null,
    ].filter(Boolean).join("&");
    dispatch(getCollectionsByDateAction(queryString, org_id, org_id_field));
  };

  useEffect(() => {
    setPage(1);
    getData(1);
  }, [dateRange, orgType, filterName]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    getData(newPage);
  };

  const tableColumns = [
    {
      title: "S/N",
      dataIndex: "id",
      render: (text, record, index) =>
        (currentPage - 1) * PAGE_SIZE + (index + 1),
      summary: "Totals",
    },
    {
      title: "Payment Date",
      dataIndex: "date",
      summary: "-",
    },
    {
      title: "Amount Paid ₦",
      dataIndex: "amount",
      render: (text) => Number(text || 0).toLocaleString(),
      summary: totals ? Number(totals.total_amount_paid).toLocaleString() : "-",
    },
    {
      title: "Monthly Installment ₦",
      dataIndex: "scheduled_amount",
      render: (text) => Number(text || 0).toLocaleString(),
      summary: totals ? Number(totals.total_installment).toLocaleString() : "-",
    },
    {
      title: "Client",
      dataIndex: "client_name",
      render: (text, record) => (
        <Link to={`/loans/${record.loan_id}`}>
          <i className="fa fa-link" aria-hidden="true" /> {text}
        </Link>
      ),
      summary: "-",
    },
    {
      title: "Handler",
      dataIndex: "handler_name",
      summary: "-",
    },
  ];

  return (
    <Container fluid className="collections-by-date-list">
      <h3 className="page-title mb-4">{fullListName}</h3>

      <Row>
        <Col>
          <Input
            style={{ width: "100%" }}
            className="mb-3"
            placeholder="Filter by client name"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
        </Col>
      </Row>

      <Row>
        <Col className="d-flex">
          {renderFilter()}
          <DatePicker.RangePicker
            allowClear={false}
            style={{ width: "100%" }}
            value={dateRange}
            onChange={setDateRange}
            className="mb-3"
          />
        </Col>
      </Row>

      {loadingCollections && (
        <div className="d-flex w-100 justify-content-center align-items-center my-2">
          <i className="fas fa-spinner fa-spin" />
          <span className="ml-2">Loading...</span>
        </div>
      )}

      <ModifiedAntTable
        rowKey="id"
        scroll={{ x: "max-content" }}
        columns={tableColumns}
        dataSource={records}
        bordered
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: totals?.count ?? 0,
          hideOnSinglePage: true,
          onChange: handlePageChange,
        }}
        showSummary={true}
      />
    </Container>
  );
};

export { CollectionsByDateList };
